if (!customElements.get('ring-animation')) {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const DEG = Math.PI / 180;
  const CX = 354.813, CY = 340.426;
  const SRX = 353.4, SRY = 339.0;
  const STROKE_W = 3;
  const EASE_AMOUNT = 0.45;
  const SWING_DEFAULT = 70;
  const S1_BFC_ON = 6, S1_BFC_OFF = 34;
  const S1_SWING_ON = 10, S1_SWING_OFF = 30, S1_SWING_END = 70;
  const S2_BFC_ON = 36, S2_BFC_OFF = 78;
  const S2_SWING_ON = 50, S2_SWING_OFF = 60, S2_SWING_END = 100;
  const TOTAL_DEG = 360;

  function easeInOutMap(accum, total) {
    if (accum >= total) return total;
    const t = accum / total;
    const s = t * t * (3 - 2 * t);
    return (EASE_AMOUNT * s + (1 - EASE_AMOUNT) * t) * total;
  }

  function rotX(n, a) { const c = Math.cos(a), s = Math.sin(a); return [n[0], n[1]*c - n[2]*s, n[1]*s + n[2]*c]; }
  function rotY(n, a) { const c = Math.cos(a), s = Math.sin(a); return [n[0]*c + n[2]*s, n[1], -n[0]*s + n[2]*c]; }
  function rotZ(n, a) { const c = Math.cos(a), s = Math.sin(a); return [n[0]*c - n[1]*s, n[0]*s + n[1]*c, n[2]]; }

  function ringFrame(lonDeg, latDeg, axisTiltXDeg, axisTiltZDeg) {
    let n = [0,0,1], u = [1,0,0], v = [0,1,0];
    const latR = latDeg*DEG, lonR = lonDeg*DEG;
    const axR = axisTiltXDeg*DEG, azR = axisTiltZDeg*DEG;
    n = rotX(n,latR); u = rotX(u,latR); v = rotX(v,latR);
    n = rotY(n,lonR); u = rotY(u,lonR); v = rotY(v,lonR);
    n = rotX(n,axR);  u = rotX(u,axR);  v = rotX(v,axR);
    n = rotZ(n,azR);  u = rotZ(u,azR);  v = rotZ(v,azR);
    return { normal: n, ux: u[0], uy: u[1], uz: u[2], vx: v[0], vy: v[1], vz: v[2] };
  }

  function projectRing(frame) {
    const { ux, uy, vx, vy } = frame;
    const m11 = SRX*ux, m12 = SRX*vx, m21 = SRY*uy, m22 = SRY*vy;
    const a = m11*m11 + m12*m12, b = m11*m21 + m12*m22, c = m21*m21 + m22*m22;
    const trace = a+c, det = a*c - b*b;
    const disc = Math.sqrt(Math.max(0, trace*trace/4 - det));
    const l1 = trace/2 + disc, l2 = trace/2 - disc;
    let angle;
    if (Math.abs(b) > 0.0001) angle = Math.atan2(l1-a, b);
    else angle = a >= c ? 0 : Math.PI/2;
    return { rx: Math.sqrt(l1), ry: Math.sqrt(Math.max(0,l2)), angle: angle*180/Math.PI };
  }

  function swingRamp(pct, onPct, offPct, startAmp, endAmp) {
    if (pct <= onPct) return null;
    if (pct >= offPct) return endAmp;
    const r = (pct - onPct) / (offPct - onPct);
    return startAmp + (endAmp - startAmp) * r * r * (3 - 2*r);
  }

  function buildDashPaths(frame, dashDeg, gapDeg, cSolidNormal) {
    const { ux, uy, uz, vx, vy, vz } = frame;
    const stepDeg = dashDeg + gapDeg;
    const numDashes = Math.floor(360 / stepDeg);
    const frontSegs = [], backSegs = [];
    const STEPS = 16;
    for (let i = 0; i < numDashes; i++) {
      const startDeg = i * stepDeg;
      const endDeg = startDeg + dashDeg;
      const midT = ((startDeg + endDeg) / 2) * DEG;
      const dot = (uz*Math.cos(midT) + vz*Math.sin(midT)) * cSolidNormal[2]
                + (ux*Math.cos(midT) + vx*Math.sin(midT)) * cSolidNormal[0]
                + (uy*Math.cos(midT) + vy*Math.sin(midT)) * cSolidNormal[1];
      let d = '';
      for (let j = 0; j <= STEPS; j++) {
        const t = (startDeg + (endDeg - startDeg) * j / STEPS) * DEG;
        const cosT = Math.cos(t), sinT = Math.sin(t);
        const x = CX + (ux*cosT + vx*sinT) * SRX;
        const y = CY + (uy*cosT + vy*sinT) * SRY;
        d += (j === 0 ? 'M' : 'L') + x.toFixed(2) + ',' + y.toFixed(2);
      }
      if (dot > 0) frontSegs.push(d);
      else backSegs.push(d);
    }
    return { front: frontSegs.join(' '), back: backSegs.join(' ') };
  }

  class RingAnimation extends HTMLElement {
    constructor() {
      super();
      this._accum = 0;
      this._rafId = null;
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._els = {};
      this._maskId = 'dash-mask-' + Math.random().toString(36).substr(2, 9);
      this._init();
    }

    connectedCallback() {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      this._onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    _init() {
      this._svg = this.querySelector('svg');
      if (!this._svg) return;
      this._svg.innerHTML = '';
      this._svg.setAttribute('viewBox', '0 0 710 681');
      this._svg.style.overflow = 'visible';
      this._buildSVG();
    }

    _onResize() {
      this._onScroll();
    }

    _onScroll() {
      const parent = this.parentElement;
      const rect = parent ? parent.getBoundingClientRect() : this.getBoundingClientRect();
      const parentH = parent ? parent.offsetHeight : this.offsetHeight;
      const totalScrollable = parentH - window.innerHeight;

      let pct = 0;
      if (totalScrollable > 0) {
        pct = Math.max(0, Math.min(1, -rect.top / totalScrollable));
      } else {
        const docH = document.documentElement.scrollHeight - window.innerHeight;
        pct = docH > 0 ? Math.max(0, Math.min(1, window.scrollY / docH)) : 0;
      }

      this._accum = easeInOutMap(pct * TOTAL_DEG, TOTAL_DEG);
      if (!this._rafId) {
        this._rafId = requestAnimationFrame(() => {
          this._update();
          this._rafId = null;
        });
      }
    }

    _update() {
      const accum = this._accum;
      const pct = (accum / TOTAL_DEG) * 100;
      const els = this._els;

      let bfc1 = 0;
      if (pct > S1_BFC_ON && pct < S1_BFC_OFF) {
        const mid = (S1_BFC_ON + S1_BFC_OFF) / 2;
        const half = (S1_BFC_OFF - S1_BFC_ON) / 2;
        bfc1 = (1 - Math.abs(pct - mid) / half);
      }

      let bfc2 = 0;
      if (pct > S2_BFC_ON && pct < S2_BFC_OFF) {
        const mid = (S2_BFC_ON + S2_BFC_OFF) / 2;
        const half = (S2_BFC_OFF - S2_BFC_ON) / 2;
        bfc2 = (1 - Math.abs(pct - mid) / half);
      }

      const amp1 = swingRamp(pct, S1_SWING_ON, S1_SWING_OFF, SWING_DEFAULT, S1_SWING_END);
      const amp2 = swingRamp(pct, S2_SWING_ON, S2_SWING_OFF, SWING_DEFAULT, S2_SWING_END);

      let swingO = SWING_DEFAULT, swingC = SWING_DEFAULT;
      if (amp2 !== null)      { swingO = amp2; swingC = amp2; }
      else if (amp1 !== null) { swingO = amp1; swingC = amp1; }

      const oLon = 1.05 * accum;
      const oLat = swingO * Math.sin(oLon * DEG);
      const oTiltX = (swingO / 2) * Math.sin(oLon * DEG);
      const oTiltZ = (swingO / 3) * Math.cos(oLon * DEG);

      const cLon = 0.95 * accum;
      const cLat = -swingC * Math.sin(cLon * DEG);
      const cTiltX = (-swingC / 2) * Math.sin(cLon * DEG);
      const cTiltZ = (-swingC / 3) * Math.cos(cLon * DEG);

      const oFrame = ringFrame(oLon, oLat, oTiltX, oTiltZ);
      const cFrame = ringFrame(cLon, cLat, cTiltX, cTiltZ);

      const oProj = projectRing(oFrame);
      const cProj = projectRing(cFrame);

      this._setProj(els.oSolid, oProj);
      this._setProj(els.cSolid, cProj);

      this._setProj(els.maskEllipse, cProj);

      const oDashes = buildDashPaths(oFrame, 1.5, 3.5, cFrame.normal);
      const cDashes = buildDashPaths(cFrame, 1.5, 3.5, oFrame.normal);

      els.oDashFront.setAttribute('d', oDashes.front);
      els.oDashBack.setAttribute('d', oDashes.back);
      els.cDashFront.setAttribute('d', cDashes.front);
      els.cDashBack.setAttribute('d', cDashes.back);

      const oSolidBack  = oFrame.normal[2] < 0;
      const cSolidBack  = cFrame.normal[2] < 0;

      els.oSolid.style.opacity = oSolidBack ? '0.18' : '1';
      els.cSolid.style.opacity = cSolidBack ? '0.18' : '1';

      if (bfc1 > 0) {
        els.oDashFront.style.opacity = (1 - bfc1 * 0.82).toFixed(2);
        els.cDashFront.style.opacity = (1 - bfc1 * 0.82).toFixed(2);
      } else if (bfc2 > 0) {
        els.oDashFront.style.opacity = (1 - bfc2 * 0.82).toFixed(2);
        els.cDashFront.style.opacity = (1 - bfc2 * 0.82).toFixed(2);
      } else {
        els.oDashFront.style.opacity = '1';
        els.cDashFront.style.opacity = '1';
      }

      els.oDashBackG.style.opacity = '0.35';
      els.cDashBackG.style.opacity = '0.35';
    }

    _buildSVG() {
      const svg = this._svg;
      const els = this._els;
      const stroke = 'currentColor';
      const maskId = this._maskId;

      const defs = document.createElementNS(SVG_NS, 'defs');
      const mask = document.createElementNS(SVG_NS, 'mask');
      mask.setAttribute('id', maskId);
      mask.setAttribute('maskUnits', 'userSpaceOnUse');
      mask.setAttribute('x', '0'); mask.setAttribute('y', '0');
      mask.setAttribute('width', '710'); mask.setAttribute('height', '681');
      const maskRect = document.createElementNS(SVG_NS, 'rect');
      maskRect.setAttribute('x', '0'); maskRect.setAttribute('y', '0');
      maskRect.setAttribute('width', '710'); maskRect.setAttribute('height', '681');
      maskRect.setAttribute('fill', 'white');
      mask.appendChild(maskRect);
      els.maskEllipse = document.createElementNS(SVG_NS, 'ellipse');
      els.maskEllipse.setAttribute('fill', 'black');
      mask.appendChild(els.maskEllipse);
      defs.appendChild(mask);
      svg.appendChild(defs);

      els.oSolid = this._makeSolid(stroke);

      els.oDashBackG = document.createElementNS(SVG_NS, 'g');
      els.oDashBackG.setAttribute('mask', `url(#${maskId})`);
      els.oDashBack = this._makeDash(stroke);
      els.oDashBackG.appendChild(els.oDashBack);
      svg.appendChild(els.oDashBackG);

      els.cDashBackG = document.createElementNS(SVG_NS, 'g');
      els.cDashBackG.setAttribute('mask', `url(#${maskId})`);
      els.cDashBack = this._makeDash(stroke);
      els.cDashBackG.appendChild(els.cDashBack);
      svg.appendChild(els.cDashBackG);

      els.cSolid = this._makeSolid(stroke);
      els.oDashFront = this._makeDash(stroke);
      svg.appendChild(els.oDashFront);
      els.cDashFront = this._makeDash(stroke);
      svg.appendChild(els.cDashFront);
    }

    _makeSolid(stroke) {
      const el = document.createElementNS(SVG_NS, 'ellipse');
      el.setAttribute('fill', 'none');
      el.setAttribute('stroke', stroke);
      el.setAttribute('stroke-width', STROKE_W);
      this._svg.appendChild(el);
      return el;
    }

    _makeDash(stroke) {
      const el = document.createElementNS(SVG_NS, 'path');
      el.setAttribute('fill', 'none');
      el.setAttribute('stroke', stroke);
      el.setAttribute('stroke-width', STROKE_W);
      el.setAttribute('stroke-linecap', 'round');
      return el;
    }

    _setProj(el, proj) {
      el.setAttribute('cx', CX);
      el.setAttribute('cy', CY);
      el.setAttribute('rx', proj.rx);
      el.setAttribute('ry', proj.ry);
      el.setAttribute('transform', `rotate(${proj.angle} ${CX} ${CY})`);
    }
  }

  customElements.define('ring-animation', RingAnimation);
}